import React from 'react'
import { Alert, Box, Button, Stack, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom';
import Viewer from "react-viewer";
import { LoadingSection, NotFoundSection, onlyOfficePreviableTypes, OtpInput, threeDModalTypes } from '../../components'
import { useGenerateOTPForAccessMutation, useGetSharedDocumentInfoMutation, useValidateOTPForDocumentAccessMutation } from '../../redux/services'
import { enqueueSnackbar } from 'notistack'
import { ThreeDViewer } from '../../components/3dViewer';
import { Viewer as PDFViewer, Worker } from "@react-pdf-viewer/core";

// Import styles
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/highlight/lib/styles/index.css";
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';

const ViewSharedDocument = () => {
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        renderToolbar: (Toolbar) => (
            <Toolbar>
                {(slots) => {
                    const {
                        EnterFullScreen,
                        ShowSearchPopover,
                        ShowProperties,
                        SwitchTheme,
                        Zoom,
                        GoToNextPage,
                        GoToPreviousPage,
                        CurrentPageInput,
                        NumberOfPages,
                        ZoomIn,
                        ZoomOut,
                    } = slots;

                    return (
                        <>
                            <div className="rpv-toolbar__left">
                                <ShowSearchPopover />
                                <ShowProperties />
                                <SwitchTheme />
                            </div>
                            <div className="rpv-toolbar__center">
                                <GoToPreviousPage />
                                <CurrentPageInput /> / <NumberOfPages />
                                <GoToNextPage />
                                <ZoomOut />
                                <Zoom />
                                <ZoomIn />
                            </div>
                            <div className="rpv-toolbar__right">
                                <EnterFullScreen />
                            </div>
                        </>
                    );
                }}
            </Toolbar>
        ),
    });

    const [getDocumentInfo, sharedDocInfo] = useGetSharedDocumentInfoMutation();
    const docInfo = sharedDocInfo?.data;
    const [generateOTP, otpResponse] = useGenerateOTPForAccessMutation();
    const [validateOTP, docResponse] = useValidateOTPForDocumentAccessMutation();

    const [searchParams, setSearchParams] = useSearchParams();

    const [otp, setOTP] = React.useState(0);
    const [generatedToken, setToken] = React.useState('');

    const submitOtp = (otp: number) => {
        validateOTP({ otp, token: generatedToken })
            .unwrap()
            .then((res) => {
                if (res?._id) {
                    setSearchParams({ token: generatedToken, otpVerified: true } as any)
                }
            })
            .catch((error) => {
                enqueueSnackbar(error?.title ?? "OTP Validation failed", { variant: "error" });
            });
    };

    const handleDownload = (response: any) => {
        const downloadLink = document.createElement('a');
        downloadLink.href = response.url;
        downloadLink.download = response.file_name;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        return <Box
            sx={{
                display: "flex",
                height: "100%",
                alignItems: "center",
                justifyContent: "center"
            }}><Alert
                severity="success"
            >
                {response.file_name} document downloaded successfully.
            </Alert></Box>;
    }

    React.useEffect(() => {
        if (generatedToken) {
            console.log("generatedToken", generatedToken);
            const getInfo = async () => {
                await getDocumentInfo({ token: generatedToken }).unwrap().catch((error) => {
                    enqueueSnackbar(error?.title ?? "Unable to get the shared document info", { variant: "error" });
                });
            };
            getInfo();
            generateOTP({ token: generatedToken }).unwrap().catch((error) => {
                enqueueSnackbar(error?.title ?? "Unable to generate the OTP", { variant: "error" });
            });
        }
        // eslint-disable-next-line
    }, [generatedToken])

    React.useEffect(() => {
        const token = searchParams.get("token");
        if (token) {
            setToken(token)
        }
        // eslint-disable-next-line
    }, [])

    const getComponent = () => {
        const hasValidated = Boolean(searchParams.get("otpVerified") || false);

        if (hasValidated && generatedToken && docResponse.isSuccess) {
            const response = docResponse?.data;
            if (docInfo?.access_permission === "view") {
                const fileType = response?.file_name?.split('.')?.pop();
                if (onlyOfficePreviableTypes.includes(fileType?.toLowerCase() ?? "")) {
                    return handleDownload(response);
                } else if (threeDModalTypes.includes(fileType?.toLowerCase() ?? "")) {
                    return <Box
                        sx={{
                            display: "flex",
                            height: "100%",
                            alignItems: "center",
                            justifyContent: "center"
                        }}><ThreeDViewer url={response.url} file_name={response?.file_name} /></Box>
                } else if (fileType?.toLowerCase() === 'pdf') {
                    return <div style={{ display: "flex", alignItems: 'center', justifyContent: 'center', width: "100%", height: "100%" }}>
                        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                            <div style={{ width: "98%", height: "100%" }}>
                                <PDFViewer
                                    fileUrl={response.url as any}
                                    plugins={[
                                        defaultLayoutPluginInstance,
                                    ]}
                                    onDocumentLoad={(e: any) => {

                                    }}
                                />
                            </div>
                        </Worker>
                    </div>
                } else {
                    return <Viewer
                        downloadInNewWindow={false}
                        downloadable={false}
                        visible={true}
                        noClose
                        onClose={() => { }}
                        images={[{ src: response.url, alt: response.file_name }]}
                        changeable={false}
                    />
                }
            } else {
                return handleDownload(response)
            }
        } else if (generatedToken) {
            if (otpResponse.isLoading) {
                return <LoadingSection message={`Generating OTP for the ${docInfo?.file_name ? `${docInfo?.file_name}` : ""} document. Please check your mail ${docInfo?.receiver_email ? `${docInfo?.receiver_email}` : ""} ...`} />
            }

            if (otpResponse.isError) {
                return <Box
                    sx={{
                        display: "flex",
                        height: "100%",
                        alignItems: "center",
                        justifyContent: "center"
                    }}><Alert
                        severity="error"
                        action={
                            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                                Retry
                            </Button>
                        }
                    >
                        Generating OTP failed please click the button to retry.
                    </Alert></Box>;
            }

            if (docResponse.isLoading) {
                return <LoadingSection message={'Validating OTP...'} />
            }

            if (docResponse.isError) {
                return <Box
                    sx={{
                        display: "flex",
                        height: "100%",
                        alignItems: "center",
                        justifyContent: "center"
                    }}><Alert
                        severity="error"
                        action={
                            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                                Retry
                            </Button>
                        }
                    >
                        OTP Validation failed please retry after some time.
                    </Alert></Box>;
            }

            return (
                <Stack
                    spacing={4}
                    sx={{
                        height: "100%",
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                    <Typography sx={{ textAlign: "center" }} variant='subtitle1'>Please enter the OTP to {docInfo?.access_permission} the "{docInfo?.file_name}" document send by {docInfo?.sender_email}</Typography>
                    <OtpInput length={6} onOtpSubmit={(value: number) => {
                        setOTP(value);
                    }} />
                    <Button variant='contained' onClick={() => submitOtp(otp)}>Submit</Button>
                </Stack>
            );
        } else {
            return <NotFoundSection />
        }
    };

    return getComponent()
}

export default ViewSharedDocument;